import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  #pending = false;
  #selectingVariant = false;
  #variantRequest = 0;
  /** @type {MutationObserver | null} */
  #overlayObserver = null;
  #purchaseObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#overlayObserver = new MutationObserver(this.#syncOverlayState);
    this.#overlayObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'hidden', 'aria-expanded', 'style', 'class'] });
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#purchaseObserver = new MutationObserver(this.#syncPurchaseState);
      this.#purchaseObserver.observe(productForm, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
    }
    this.#syncPurchaseState();
    document.addEventListener('focusin', this.#syncOverlayState, { signal });
    document.addEventListener('focusout', this.#syncOverlayState, { signal });
    window.visualViewport?.addEventListener('resize', this.#syncOverlayState, { signal });
    window.addEventListener('pageshow', this.#syncOverlayState, { signal });
    this.#syncOverlayState();

    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      this.#syncOverlayState();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    this.#purchaseObserver?.disconnect();
    this.#abortController.abort();
    this.#variantRequest += 1;
    clearTimeout(this.#resetTimeout);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom <= 0) {
          if (this.#hiddenByBottom) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        this.#hiddenByBottom = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.#hideStickyBar();
        } else if (buyButtonsBlock.getBoundingClientRect().bottom <= 0) {
          this.#showStickyBar();
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    if (footer) this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    this.#syncPurchaseState();
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
    const button = this.#targetAddToCartButton;
    if (!button || this.refs.addToCartButton.disabled) return;
    const form = productForm.querySelector('form');
    if (!form?.reportValidity()) return;

    clearTimeout(this.#resetTimeout);
    this.querySelector('[data-sticky-status]').textContent = '';
    this.refs.addToCartButton.removeAttribute('data-added');
    button.dataset.puppet = 'true';
    // Submit the original form so quantity rules, variants and drawer updates stay native.
    button.click();
    if (!this.#pending) {
      button.dataset.puppet = 'false';
      const error = productForm.querySelector('[ref="addToCartTextError"]');
      this.querySelector('[data-sticky-status]').textContent = error?.textContent?.trim() || '';
    }
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#selectingVariant = true;
    const request = ++this.#variantRequest;
    this.refs.addToCartButton.disabled = true;

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (request !== this.#variantRequest || this.#abortController.signal.aborted) return;
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && String(productId) !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;
        if (this.#pending) this.refs.addToCartButton.setAttribute('aria-busy', 'true');

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = String(variant.id);
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      })
      .finally(() => {
        if (request !== this.#variantRequest || this.#abortController.signal.aborted) return;
        this.#selectingVariant = false;
        this.#syncPurchaseState();
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    const productForm = this.#getProductForm();
    if (!(event.target instanceof Element) || !productForm?.contains(event.target)) return;
    const status = this.querySelector('[data-sticky-status]');
    if (!('promise' in event)) {
      if (this.#pending) status.textContent = event.detail?.description || event.error || '';
      return;
    }
    // Only mirror an add initiated through this bar, not unrelated cart edits.
    if (this.#targetAddToCartButton?.dataset.puppet !== 'true' || this.#pending) return;
    this.#pending = true;
    this.refs.addToCartButton.disabled = true;
    this.refs.addToCartButton.setAttribute('aria-busy', 'true');
    const finish = () => {
      this.#pending = false;
      if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
      this.refs.addToCartButton.removeAttribute('aria-busy');
      this.#syncPurchaseState();
    };
    event.promise.then(({ detail }) => {
      if (this.#abortController.signal.aborted) return;
      if (detail?.didError) {
        status.textContent ||= productForm.querySelector('[ref="addToCartTextError"]')?.textContent?.trim() || '';
      } else {
        this.refs.addToCartButton.dataset.added = 'true';
        status.textContent = Theme.translations.added;
        clearTimeout(this.#resetTimeout);
        this.#resetTimeout = setTimeout(() => {
          this.refs.addToCartButton.removeAttribute('data-added');
          status.textContent = '';
        }, 2500);
      }
    }, (error) => {
      if (!this.#abortController.signal.aborted) status.textContent = error?.message || '';
    }).finally(finish);
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine || !(event.target instanceof Element) || !this.#getProductForm()?.contains(event.target)) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#syncOverlayState();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    stickyBar.inert = true;
  }

  #syncOverlayState = () => {
    const dialogOrMenu = document.querySelector('dialog[open], theme-drawer[open], header-drawer details[open], [data-n4-language][open]');
    const privacyBanner = document.querySelector('#shopify-pc__banner');
    const chatRegistered = Boolean(customElements.get('shopify-chat') && document.querySelector('shopify-chat'));
    this.dataset.chatActive = String(chatRegistered);
    const active = document.activeElement;
    const editing = active instanceof Element && active.matches('input, textarea, select, [contenteditable="true"]');
    const viewport = window.visualViewport;
    const keyboard = viewport && viewport.scale === 1 && window.innerHeight - viewport.height > 150;
    const chatOpen = chatRegistered && document.querySelector('shopify-chat[open]');
    const obstructed = Boolean(dialogOrMenu || editing || keyboard || chatOpen || privacyBanner?.getClientRects().length);
    this.dataset.obstructed = String(obstructed);
    this.refs.stickyBar.inert = obstructed || !this.#isStuck;
  };

  #syncPurchaseState = () => {
    const button = this.#getProductForm()?.querySelector('[ref="addToCartButton"]');
    const disabled = !button || button.disabled || this.#pending || this.#selectingVariant || this.dataset.variantAvailable !== 'true';
    this.refs.addToCartButton.disabled = disabled;
    if (button) {
      const copy = button.cloneNode(true);
      copy.querySelectorAll('svg, .svg-wrapper, .add-to-cart__added').forEach(node => node.remove());
      const label = this.querySelector('.add-to-cart-text__content > span:first-child');
      const text = copy.textContent.trim();
      if (label && label.textContent.trim() !== text) label.textContent = text;
    }
    this.#updateButtonText();
  };

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
